import {useEffect, useMemo} from 'react'
import {VisualizerGraphDataType} from '../../../shared/db'
import {
  createDispatchActions,
  selectMethod,
  selectParticipantTimeSeriesData,
  selectProjectSettings,
  selectProjectDataDigest,
} from '../../../store'
import {GraphDataTypeMap} from '../../../components/charts/utils/utils'
import _ from 'lodash'
import {TimeSeriesDataLoaderProps, filterVisualizerSidebarSetting, convertToYYMMIndex} from './utils'
import {useCurrentProjectState} from '../../custom_hooks'

export const GarminConnectDataLoader = (props: TimeSeriesDataLoaderProps) => {
  const {participantId, loadDataYYMMDDIndex} = props
  const {projectId} = useCurrentProjectState()
  const projectSettings = projectId ? selectProjectSettings()[projectId] : undefined
  const method = selectMethod()
  const garminConnectEnabled: boolean = method?.garminConnectEnable
  const projectDataDigest = projectId ? selectProjectDataDigest()?.[projectId] : undefined
  const currentParticipantTimeSeriesData = selectParticipantTimeSeriesData(participantId)

  const {doREQUEST_VISUALIZER_GARMIN_CONNECT_DATA_FETCH, doREQUEST_PROJECT_DATA_DIGEST} = createDispatchActions()

  const selectedGarminConnectDataList = useMemo<VisualizerGraphDataType[]>(() => {
    if (garminConnectEnabled && projectSettings?.visualizerSidebarSetting.TimeSeries) {
      const scope = [
        VisualizerGraphDataType.GarminConnectStress,
        VisualizerGraphDataType.GarminConnectHeartRate,
        VisualizerGraphDataType.GarminConnectRestingHR,
        VisualizerGraphDataType.GarminConnectBodyBattery,
        VisualizerGraphDataType.GarminConnectSleepStaging,
        VisualizerGraphDataType.GarminConnectSkinTemperature,
        VisualizerGraphDataType.GarminConnectActivityEpochs,
        VisualizerGraphDataType.GarminConnectSteps,
        VisualizerGraphDataType.GarminConnectCalories,
      ]
      const graphDataList = filterVisualizerSidebarSetting(projectSettings?.visualizerSidebarSetting.TimeSeries, scope)
      if (!graphDataList.includes(VisualizerGraphDataType.GarminConnectHeartRate)) {
        // always include VisualizerGraphDataType.GarminConnectHeartRate for default task time line data
        graphDataList.push(VisualizerGraphDataType.GarminConnectHeartRate)
      }
      return graphDataList
    } else {
      return []
    }
  }, [garminConnectEnabled, projectSettings?.visualizerSidebarSetting.TimeSeries])

  useEffect(() => {
    // attempt to load Garmin Connect data
    if (selectedGarminConnectDataList.length) {
      const requestDigestYymmIndexList: number[] = []
      for (const yymmddIndex of loadDataYYMMDDIndex) {
        const yymmIndex = convertToYYMMIndex(yymmddIndex)
        if (projectDataDigest?.coveredYYMMIndex?.includes(yymmIndex)) {
          optimisedFetchGarminConnectData(yymmddIndex)
        } else {
          fetchGarminConnectData(yymmddIndex)
          if (!requestDigestYymmIndexList.includes(yymmIndex)) {
            requestDigestYymmIndexList.push(yymmIndex)
          }
        }
      }

      if (requestDigestYymmIndexList.length > 0) {
        fetchGarminConnectDataDigest(requestDigestYymmIndexList)
      }
    }
  }, [participantId, loadDataYYMMDDIndex, selectedGarminConnectDataList])

  const optimisedFetchGarminConnectData = (yymmddIndex: number) => {
    const garminConnectDataDigest = projectDataDigest?.participantDataDigestMap?.[participantId]?.garminConnectDataDates
    if (garminConnectDataDigest && garminConnectDataDigest.includes(yymmddIndex)) {
      fetchGarminConnectData(yymmddIndex)
    }
  }

  const fetchGarminConnectData = (yymmddIndex: number) => {
    const timeSeriesData = currentParticipantTimeSeriesData?.data
    const garminConnectHookTypeList = selectedGarminConnectDataList
      .filter((graphType) => !timeSeriesData || timeSeriesData[graphType]?.[yymmddIndex] === undefined)
      .map((graphType) => GraphDataTypeMap[graphType])

    if (garminConnectHookTypeList.length > 0) {
      doREQUEST_VISUALIZER_GARMIN_CONNECT_DATA_FETCH({
        payload: {
          participantId: participantId,
          yymmddIndex: yymmddIndex,
          dataTypeList: [...new Set(garminConnectHookTypeList)],
        },
      })
    }
  }

  const fetchGarminConnectDataDigest = (yymmIndexList: number[]) => {
    doREQUEST_PROJECT_DATA_DIGEST({
      payload: {
        projectId,
        yymmIndexList,
        requestTaskData: false,
        requestScheduleTaskData: false,
        requestGarminData: true,
        requestDexcomData: false,
      },
    })
  }
}
